import { type Fabric, FabricSide, type FabricTextureStack } from '@seddi/scene';
import { type ISurfaceMaterial, type SurfaceMaterialModel } from '@seddi/surface-material';
import { SHADING_MODEL_DEFAULT } from '@seddi/ui-babylon-core';

export interface DoubleSideSurfaceMaterial {
  back?: ISurfaceMaterial;
  front: ISurfaceMaterial;
}

/**
 * Converts a fabric stack to a surface material.
 * @param stack - The fabric stack.
 */
function adaptStack(stack: FabricTextureStack): ISurfaceMaterial {
  const material: ISurfaceMaterial = {
    id: stack.id,
    model: stack.model,
    name: stack.name,
    params: stack.params,
    repeat: {
      x: 1.0 / stack.size.x,
      y: 1.0 / stack.size.y,
    },
    tags: ['FABRIC'],
  };
  return material;
}

/**
 * Converts a fabric to a double sided surface material.
 * @param fabric - Fabric to adapt.
 * @param model - Preferred shading model.
 */
function adaptFabric(
  fabric: Fabric,
  model: SurfaceMaterialModel = SHADING_MODEL_DEFAULT,
): DoubleSideSurfaceMaterial {
  const double = {} as DoubleSideSurfaceMaterial;

  for (const stack of fabric.textures) {
    if (stack.model === model) {
      const material = adaptStack(stack);

      switch (stack.fabricSide) {
        case FabricSide.FRONT: {
          double.front = material;
          break;
        }
        case FabricSide.BACK: {
          double.back = material;
          break;
        }
      }
    }
  }

  return double;
}

/**
 * Class for adapting a fabric to a double sided surface material.
 */
export const FabricAdapter = {
  adapt: adaptFabric,
};
